import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    ArrowLeftIcon,
    ChatBubbleLeftIcon,
    ClockIcon,
    UserCircleIcon,
    PaperClipIcon,
    LinkIcon,
    DocumentDuplicateIcon,
    XMarkIcon,
    EyeIcon,
    BoltIcon,
    ArrowPathIcon,
    ShieldExclamationIcon,
    ExclamationTriangleIcon,
    PlusIcon,
    UserPlusIcon,
    TagIcon,
    CodeBracketIcon,
    PhotoIcon,
    DocumentTextIcon,
    DocumentIcon,
    ArrowTopRightOnSquareIcon,
    CheckCircleIcon,
    InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { Heading } from "src/components/base/heading";
import { Badge } from "src/components/base/badge";
import { Button } from "src/components/base/button";
import { Textarea } from "src/components/base/textarea";
import type { View, ViewContext } from "../types";
import { VIEW_LABELS, VIEW_COLORS } from "../types";
import { MOCK_TICKETS } from "../data/tickets";
import type { TicketPriority, TicketStatus } from "../data/tickets";
import { UNIFIED_EVENTS } from "../data/events";
import { Card } from "../shared";

// ─── Constants ───────────────────────────────────────────────────────────────

const priorityColor = { critical: "red", high: "orange", medium: "yellow", low: "zinc" } as const;
const statusColor = {
    open: "red",
    "in-progress": "amber",
    waiting: "purple",
    resolved: "green",
    closed: "zinc",
} as const;

type Tab = "overview" | "activity" | "related" | "attachments";

const TABS: { key: Tab; label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }[] = [
    { key: "overview", label: "Overview", icon: InformationCircleIcon },
    { key: "activity", label: "Activity", icon: ClockIcon },
    { key: "related", label: "Related Events", icon: LinkIcon },
    { key: "attachments", label: "Attachments", icon: PaperClipIcon },
];

const MOCK_SLA_HOURS: Record<TicketPriority, number> = {
    critical: 2,
    high: 4,
    medium: 8,
    low: 24,
};

type ActivityType = "status" | "comment" | "assignment" | "view";

interface ActivityEntry {
    id: string;
    type: ActivityType;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    iconColor: string;
    description: string;
    user: string;
    time: string;
}

interface MockAttachment {
    id: string;
    filename: string;
    type: "image" | "log" | "pdf" | "text";
    size: string;
    uploadedBy: string;
    date: string;
}

interface MockWatcher {
    name: string;
    initials: string;
    color: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseMockTimestamp(ts: string): number {
    if (ts.includes("-")) return new Date(ts.replace(" ", "T")).getTime();
    const today = new Date();
    const [h, m] = ts.split(":").map(Number);
    today.setHours(h, m, 0, 0);
    return today.getTime();
}

function getSlaInfo(
    priority: TicketPriority,
    createdAt: string,
): { percentElapsed: number; remaining: string; overdue: boolean; totalMs: number; elapsedMs: number } {
    const createdMs = parseMockTimestamp(createdAt);
    const slaHours = MOCK_SLA_HOURS[priority];
    const totalMs = slaHours * 60 * 60 * 1000;
    const now = new Date("2026-03-13T15:30:00").getTime();
    const elapsedMs = now - createdMs;
    const percentElapsed = Math.min((elapsedMs / totalMs) * 100, 150);
    const remainingMs = totalMs - elapsedMs;

    if (remainingMs <= 0) {
        const overdueMins = Math.abs(Math.round(remainingMs / 60000));
        const overHrs = Math.floor(overdueMins / 60);
        const overMin = overdueMins % 60;
        return {
            percentElapsed,
            remaining: overHrs > 0 ? `-${overHrs}h ${overMin}m` : `-${overMin}m`,
            overdue: true,
            totalMs,
            elapsedMs,
        };
    }

    const remMins = Math.round(remainingMs / 60000);
    const hrs = Math.floor(remMins / 60);
    const mins = remMins % 60;
    return { percentElapsed, remaining: hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`, overdue: false, totalMs, elapsedMs };
}

// ─── SLA Countdown Widget ────────────────────────────────────────────────────

function SlaCountdown({
    priority,
    createdAt,
    status,
}: {
    priority: TicketPriority;
    createdAt: string;
    status: TicketStatus;
}) {
    const isTerminal = status === "resolved" || status === "closed";
    const { percentElapsed, remaining, overdue, totalMs, elapsedMs } = getSlaInfo(priority, createdAt);

    let barColor = "bg-emerald-500";
    let textColor = "text-emerald-600 dark:text-emerald-400";
    let bgGlow = "shadow-emerald-500/20";
    if (isTerminal) {
        barColor = "bg-zinc-400";
        textColor = "text-zinc-500";
        bgGlow = "";
    } else if (overdue) {
        barColor = "bg-red-500 animate-pulse";
        textColor = "text-red-600 dark:text-red-400";
        bgGlow = "shadow-red-500/30";
    } else if (percentElapsed > 80) {
        barColor = "bg-red-500";
        textColor = "text-red-600 dark:text-red-400";
        bgGlow = "shadow-red-500/20";
    } else if (percentElapsed > 50) {
        barColor = "bg-amber-500";
        textColor = "text-amber-600 dark:text-amber-400";
        bgGlow = "shadow-amber-500/20";
    }

    const slaHrs = MOCK_SLA_HOURS[priority];

    return (
        <Card>
            <div className="flex items-center gap-2">
                <ClockIcon className={`h-5 w-5 ${textColor}`} />
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">SLA Countdown</h3>
            </div>
            <div className="mt-3 text-center">
                <p className={`text-3xl font-extrabold tracking-tight ${textColor}`}>
                    {isTerminal ? "Completed" : overdue ? "OVERDUE" : remaining}
                </p>
                {!isTerminal && !overdue && <p className="mt-0.5 text-xs text-zinc-500">remaining of {slaHrs}h SLA</p>}
                {overdue && !isTerminal && (
                    <p className="mt-0.5 text-xs font-semibold text-red-500">{remaining} past SLA breach</p>
                )}
            </div>
            <div className="mt-4">
                <div
                    className={`h-3 w-full overflow-hidden rounded-full bg-zinc-200 shadow-inner dark:bg-zinc-700 ${bgGlow}`}
                >
                    <div
                        className={`h-full rounded-full ${barColor} transition-all duration-700`}
                        style={{ width: `${Math.min(percentElapsed, 100)}%` }}
                    />
                </div>
                <div className="mt-1.5 flex justify-between text-[10px] text-zinc-400">
                    <span>0h</span>
                    <span>{Math.round(slaHrs / 2)}h</span>
                    <span>{slaHrs}h</span>
                </div>
            </div>
        </Card>
    );
}

// ─── Tab Bar with animated indicator ─────────────────────────────────────────

function TabBar({ activeTab, onChange }: { activeTab: Tab; onChange: (t: Tab) => void }) {
    const activeIdx = TABS.findIndex((t) => t.key === activeTab);

    return (
        <div className="relative mt-6">
            <div className="flex gap-1 rounded-xl border border-zinc-200/50 bg-zinc-50/50 p-1 dark:border-zinc-700/50 dark:bg-zinc-900/50">
                {TABS.map((tab, i) => {
                    const Icon = tab.icon;
                    const isActive = tab.key === activeTab;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => onChange(tab.key)}
                            className={`relative z-10 flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 ${
                                isActive
                                    ? "text-blue-700 dark:text-blue-300"
                                    : "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
                            }`}
                        >
                            <Icon className="h-4 w-4" />
                            {tab.label}
                        </button>
                    );
                })}
            </div>
            {/* Animated indicator */}
            <div
                className="absolute top-1 h-[calc(100%-0.5rem)] rounded-lg bg-white shadow-sm transition-all duration-300 ease-out dark:bg-zinc-800"
                style={{
                    left: `calc(${activeIdx} * 25% + 0.25rem)`,
                    width: `calc(25% - 0.5rem)`,
                }}
            />
        </div>
    );
}

// ─── Activity Timeline ───────────────────────────────────────────────────────

function ActivityTimeline({
    activities,
    filterType,
}: {
    activities: ActivityEntry[];
    filterType: ActivityType | "all";
}) {
    const filtered = filterType === "all" ? activities : activities.filter((a) => a.type === filterType);

    if (filtered.length === 0) {
        return <p className="py-6 text-center text-sm italic text-zinc-400">No activity matching this filter.</p>;
    }

    return (
        <div className="space-y-0">
            {filtered.map((entry) => {
                const Icon = entry.icon;
                return (
                    <div
                        key={entry.id}
                        className="relative border-l-2 border-zinc-200/60 py-3 pl-6 transition-all hover:bg-zinc-50/50 dark:border-zinc-700/60 dark:hover:bg-zinc-800/30"
                    >
                        <div
                            className={`absolute -left-[11px] top-4 flex h-5 w-5 items-center justify-center rounded-full ${entry.iconColor} shadow-sm`}
                        >
                            <Icon className="h-3 w-3 text-white" />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-zinc-900 dark:text-white">{entry.user}</span>
                            <span className="text-sm text-zinc-600 dark:text-zinc-400">{entry.description}</span>
                        </div>
                        <span className="mt-0.5 block font-mono text-xs text-zinc-400">{entry.time}</span>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Rich Comment Editor ─────────────────────────────────────────────────────

function RichCommentEditor() {
    const [isInternal, setIsInternal] = useState(false);
    const [commentText, setCommentText] = useState("");

    return (
        <div
            className={`mt-4 rounded-xl border transition-all ${isInternal ? "border-amber-300/60 bg-amber-50/50 dark:border-amber-700/40 dark:bg-amber-950/20" : "border-zinc-200/60 bg-white/50 dark:border-zinc-700/60 dark:bg-zinc-800/50"}`}
        >
            {/* Toolbar */}
            <div className="flex items-center gap-1 border-b border-zinc-200/60 px-3 py-2 dark:border-zinc-700/60">
                <button
                    className="rounded p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                    title="Bold"
                >
                    <span className="text-xs font-extrabold">B</span>
                </button>
                <button
                    className="rounded p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                    title="Italic"
                >
                    <span className="text-xs font-semibold italic">I</span>
                </button>
                <button
                    className="rounded p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                    title="Code"
                >
                    <CodeBracketIcon className="h-3.5 w-3.5" />
                </button>
                <button
                    className="rounded p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                    title="Link"
                >
                    <LinkIcon className="h-3.5 w-3.5" />
                </button>
                <div className="mx-2 h-4 w-px bg-zinc-200 dark:bg-zinc-700" />
                <button
                    className="rounded p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                    title="@mention"
                >
                    <span className="text-xs font-semibold">@</span>
                </button>
                <div className="flex-1" />
                <label className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 transition-colors hover:bg-amber-100/60 dark:hover:bg-amber-900/30">
                    <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-zinc-300 text-amber-500 focus:ring-amber-400"
                    />
                    <span
                        className={`text-xs font-medium ${isInternal ? "text-amber-700 dark:text-amber-400" : "text-zinc-500"}`}
                    >
                        Internal Note
                    </span>
                </label>
            </div>
            <Textarea
                rows={3}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={
                    isInternal
                        ? "Add an internal note (not visible to reporter)..."
                        : "Type a comment... Use @name to mention someone"
                }
                className="border-0 bg-transparent focus:ring-0"
            />
            <div className="flex items-center justify-between border-t border-zinc-200/60 px-3 py-2 dark:border-zinc-700/60">
                <p className="text-[10px] text-zinc-400">
                    {isInternal && (
                        <span className="font-semibold text-amber-500">
                            Internal note - visible only to team members
                        </span>
                    )}
                </p>
                <Button color={isInternal ? "amber" : "blue"} className="text-xs">
                    {isInternal ? "Add Internal Note" : "Submit Comment"}
                </Button>
            </div>
        </div>
    );
}

// ─── Attachments Tab ─────────────────────────────────────────────────────────

const MOCK_ATTACHMENTS: MockAttachment[] = [
    {
        id: "att-1",
        filename: "screenshot-alert-dashboard.png",
        type: "image",
        size: "1.2 MB",
        uploadedBy: "M. Schmidt",
        date: "2026-03-13 14:30",
    },
    {
        id: "att-2",
        filename: "endpoint-scan-edr-ws-142.log",
        type: "log",
        size: "847 KB",
        uploadedBy: "K. Fischer",
        date: "2026-03-13 14:45",
    },
    {
        id: "att-3",
        filename: "incident-response-runbook.pdf",
        type: "pdf",
        size: "2.4 MB",
        uploadedBy: "L. Weber",
        date: "2026-03-13 15:00",
    },
    {
        id: "att-4",
        filename: "network-capture-185.143.223.pcap",
        type: "text",
        size: "15.7 MB",
        uploadedBy: "L. Weber",
        date: "2026-03-13 14:25",
    },
    {
        id: "att-5",
        filename: "malware-hash-analysis.txt",
        type: "text",
        size: "12 KB",
        uploadedBy: "K. Fischer",
        date: "2026-03-13 14:50",
    },
    {
        id: "att-6",
        filename: "firewall-rule-changes.pdf",
        type: "pdf",
        size: "340 KB",
        uploadedBy: "M. Schmidt",
        date: "2026-03-13 15:10",
    },
];

function AttachmentIcon({ type }: { type: MockAttachment["type"] }) {
    switch (type) {
        case "image":
            return <PhotoIcon className="h-8 w-8 text-purple-500" />;
        case "log":
            return <DocumentTextIcon className="h-8 w-8 text-amber-500" />;
        case "pdf":
            return <DocumentIcon className="h-8 w-8 text-red-500" />;
        case "text":
            return <DocumentTextIcon className="h-8 w-8 text-blue-500" />;
    }
}

function AttachmentsTab() {
    const [previewId, setPreviewId] = useState<string | null>(null);

    return (
        <>
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                    Attachments ({MOCK_ATTACHMENTS.length})
                </h3>
                <Button color="blue" className="text-xs">
                    <PlusIcon className="h-3.5 w-3.5" />
                    Upload File
                </Button>
            </div>
            <div className="mt-4 space-y-2">
                {MOCK_ATTACHMENTS.map((att) => (
                    <div
                        key={att.id}
                        className="group flex items-center gap-4 rounded-xl border border-zinc-200/50 bg-white/60 p-3 transition-all hover:border-blue-300/50 hover:shadow-md hover:shadow-blue-500/10 dark:border-zinc-700/50 dark:bg-zinc-800/60 dark:hover:border-blue-700/50"
                    >
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-zinc-100/80 dark:bg-zinc-800/80">
                            <AttachmentIcon type={att.type} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-zinc-900 dark:text-white">{att.filename}</p>
                            <div className="mt-0.5 flex items-center gap-2 text-xs text-zinc-500">
                                <span>{att.size}</span>
                                <span>|</span>
                                <span>{att.uploadedBy}</span>
                                <span>|</span>
                                <span className="font-mono">{att.date}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                                onClick={() => setPreviewId(previewId === att.id ? null : att.id)}
                                className="rounded-lg p-2 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                                title="Preview"
                            >
                                <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                                className="rounded-lg p-2 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                                title="Download"
                            >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Preview placeholder */}
            {previewId && (
                <div className="mt-4 rounded-xl border border-zinc-200/50 bg-zinc-50/50 p-8 text-center dark:border-zinc-700/50 dark:bg-zinc-900/50">
                    <PhotoIcon className="mx-auto h-16 w-16 text-zinc-300 dark:text-zinc-600" />
                    <p className="mt-3 text-sm font-medium text-zinc-500">
                        Preview for: {MOCK_ATTACHMENTS.find((a) => a.id === previewId)?.filename}
                    </p>
                    <p className="mt-1 text-xs text-zinc-400">File preview is not available in this demo.</p>
                    <Button plain className="mt-3 text-xs" onClick={() => setPreviewId(null)}>
                        Close Preview
                    </Button>
                </div>
            )}
        </>
    );
}

// ─── Actions Sidebar ─────────────────────────────────────────────────────────

function ActionsSidebar() {
    const actions = [
        { label: "Assign", icon: UserCircleIcon, color: "text-blue-500" },
        { label: "Change Priority", icon: ExclamationTriangleIcon, color: "text-amber-500" },
        { label: "Add Watcher", icon: UserPlusIcon, color: "text-purple-500" },
        { label: "Link Event", icon: LinkIcon, color: "text-cyan-500" },
        { label: "Duplicate", icon: DocumentDuplicateIcon, color: "text-zinc-500" },
        { label: "Close Ticket", icon: XMarkIcon, color: "text-red-500" },
    ];

    return (
        <Card>
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Quick Actions</h3>
            <div className="mt-3 grid grid-cols-2 gap-2">
                {actions.map((action) => {
                    const Icon = action.icon;
                    return (
                        <button
                            key={action.label}
                            className="flex flex-col items-center gap-1.5 rounded-xl border border-zinc-200/50 bg-white/60 p-3 text-center transition-all hover:border-blue-300/50 hover:shadow-md hover:shadow-blue-500/10 dark:border-zinc-700/50 dark:bg-zinc-800/60 dark:hover:border-blue-700/50"
                        >
                            <Icon className={`h-5 w-5 ${action.color}`} />
                            <span className="text-[11px] font-medium text-zinc-700 dark:text-zinc-300">
                                {action.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </Card>
    );
}

// ─── Watchers Section ────────────────────────────────────────────────────────

const MOCK_WATCHERS: MockWatcher[] = [
    { name: "M. Schmidt", initials: "MS", color: "from-blue-400 to-blue-600" },
    { name: "L. Weber", initials: "LW", color: "from-purple-400 to-purple-600" },
    { name: "J. Hoffmann", initials: "JH", color: "from-emerald-400 to-emerald-600" },
    { name: "K. Fischer", initials: "KF", color: "from-amber-400 to-amber-600" },
];

function WatchersSection() {
    return (
        <Card>
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Watchers</h3>
                <button className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/30">
                    <UserPlusIcon className="h-3.5 w-3.5" />
                    Add
                </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
                {MOCK_WATCHERS.map((w) => (
                    <div
                        key={w.name}
                        className="group flex items-center gap-1.5 rounded-full border border-zinc-200/50 bg-white/70 py-1 pl-1 pr-3 transition-all hover:border-blue-300/50 hover:shadow-sm dark:border-zinc-700/50 dark:bg-zinc-800/70 dark:hover:border-blue-700/50"
                    >
                        <div
                            className={`flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br ${w.color} text-[9px] font-bold text-white shadow-sm`}
                        >
                            {w.initials}
                        </div>
                        <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">{w.name}</span>
                    </div>
                ))}
            </div>
        </Card>
    );
}

// ─── Linked Tickets ──────────────────────────────────────────────────────────

function LinkedTickets({
    ticketId,
    onNavigateDetail,
}: {
    ticketId: string;
    onNavigateDetail: (ctx: ViewContext) => void;
}) {
    const ticket = MOCK_TICKETS.find((t) => t.id === ticketId);
    if (!ticket) return null;

    const linked = MOCK_TICKETS.filter(
        (t) => t.id !== ticketId && t.relatedEventIds.some((eid) => ticket.relatedEventIds.includes(eid)),
    );

    if (linked.length === 0) return null;

    return (
        <Card>
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Linked Tickets</h3>
                <button className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/30">
                    <LinkIcon className="h-3.5 w-3.5" />
                    Link Ticket
                </button>
            </div>
            <div className="mt-3 space-y-2">
                {linked.map((lt) => (
                    <button
                        key={lt.id}
                        onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: lt.id })}
                        className="flex w-full items-center gap-3 rounded-xl border border-zinc-200/50 bg-white/60 p-3 text-left transition-all hover:border-blue-300/50 hover:shadow-md hover:shadow-blue-500/10 dark:border-zinc-700/50 dark:bg-zinc-800/60 dark:hover:border-blue-700/50"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-zinc-400">{lt.id}</span>
                                <Badge color={priorityColor[lt.priority]}>{lt.priority}</Badge>
                                <Badge color={statusColor[lt.status]}>{lt.status}</Badge>
                            </div>
                            <p className="mt-1 truncate text-sm font-medium text-zinc-900 dark:text-white">
                                {lt.title}
                            </p>
                        </div>
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 text-zinc-400" />
                    </button>
                ))}
            </div>
        </Card>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function TicketDetailView({
    ticketId,
    onNavigate,
    onNavigateDetail,
}: {
    ticketId: string;
    onNavigate: (v: View) => void;
    onNavigateDetail: (ctx: ViewContext) => void;
}) {
    const { t } = useTranslation();
    const ticket = MOCK_TICKETS.find((tk) => tk.id === ticketId);
    const [activeTab, setActiveTab] = useState<Tab>("overview");
    const [activityFilter, setActivityFilter] = useState<ActivityType | "all">("all");

    if (!ticket) {
        return (
            <div>
                <Button plain onClick={() => onNavigate("tickets")}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t("tickets.backToList")}
                </Button>
                <p className="mt-8 text-zinc-500">{t("tickets.notFound")}</p>
            </div>
        );
    }

    const relatedEvents = UNIFIED_EVENTS.filter((e) => ticket.relatedEventIds.includes(e.id));

    // Build full activity timeline
    const activities = useMemo<ActivityEntry[]>(() => {
        const items: ActivityEntry[] = [];

        // Status changes
        ticket.statusHistory.forEach((sh, i) => {
            items.push({
                id: `status-${i}`,
                type: "status",
                icon: ArrowPathIcon,
                iconColor: "bg-amber-500",
                description: `changed status from ${sh.from} to ${sh.to}`,
                user: sh.by,
                time: sh.time,
            });
        });

        // Comments
        ticket.comments.forEach((c) => {
            items.push({
                id: `comment-${c.id}`,
                type: "comment",
                icon: ChatBubbleLeftIcon,
                iconColor: "bg-blue-500",
                description: c.text.length > 80 ? c.text.substring(0, 80) + "..." : c.text,
                user: c.author,
                time: c.time,
            });
        });

        // Mock assignment events
        items.push({
            id: "assign-1",
            type: "assignment",
            icon: UserCircleIcon,
            iconColor: "bg-purple-500",
            description: `was assigned to ${ticket.assignee}`,
            user: ticket.reporter,
            time: ticket.createdAt,
        });

        // Mock view events
        items.push({
            id: "view-1",
            type: "view",
            icon: EyeIcon,
            iconColor: "bg-zinc-400",
            description: "viewed this ticket",
            user: "L. Weber",
            time: ticket.updatedAt,
        });
        items.push({
            id: "view-2",
            type: "view",
            icon: EyeIcon,
            iconColor: "bg-zinc-400",
            description: "viewed this ticket",
            user: "J. Hoffmann",
            time: ticket.createdAt,
        });

        // Sort by time (approximate)
        return items.sort((a, b) => b.time.localeCompare(a.time));
    }, [ticket]);

    return (
        <>
            {/* ── Back button ────────────────────────────────── */}
            <Button plain onClick={() => onNavigate("tickets")}>
                <ArrowLeftIcon className="h-4 w-4" />
                {t("tickets.backToList")}
            </Button>

            {/* ── Header ─────────────────────────────────────── */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="font-mono text-sm text-zinc-400">{ticket.id}</span>
                <Heading>{ticket.title}</Heading>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
                <Badge color={priorityColor[ticket.priority]}>{ticket.priority}</Badge>
                <Badge color={statusColor[ticket.status]}>{ticket.status}</Badge>
                <Badge color="zinc">{ticket.system.toUpperCase()}</Badge>
            </div>

            {/* ── Tabs ───────────────────────────────────────── */}
            <TabBar activeTab={activeTab} onChange={setActiveTab} />

            {/* ── Content ────────────────────────────────────── */}
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                    {/* ── Overview Tab ────────────────────────── */}
                    {activeTab === "overview" && (
                        <>
                            <Card>
                                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                    {t("tickets.description")}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                    {ticket.description}
                                </p>
                            </Card>
                            <Card>
                                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                    {t("tickets.comments")} ({ticket.comments.length})
                                </h3>
                                <div className="mt-4 space-y-0">
                                    {ticket.comments.length === 0 && (
                                        <p className="text-sm italic text-zinc-400">{t("tickets.noComments")}</p>
                                    )}
                                    {ticket.comments.map((comment) => (
                                        <div
                                            key={comment.id}
                                            className="relative border-l-2 border-blue-200/60 py-3 pl-5 dark:border-blue-800/60"
                                        >
                                            <div className="absolute -left-[5px] top-4 h-2.5 w-2.5 rounded-full bg-blue-400 shadow-sm shadow-blue-400/50" />
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-purple-500 text-[9px] font-bold text-white shadow-sm">
                                                    {comment.author
                                                        .split(" ")
                                                        .map((n) => n[0])
                                                        .join("")}
                                                </div>
                                                <span className="text-sm font-medium text-zinc-900 dark:text-white">
                                                    {comment.author}
                                                </span>
                                                <span className="font-mono text-xs text-zinc-400">{comment.time}</span>
                                            </div>
                                            <p className="mt-1 pl-8 text-sm text-zinc-600 dark:text-zinc-400">
                                                {comment.text}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                                <RichCommentEditor />
                            </Card>
                        </>
                    )}

                    {/* ── Activity Tab ────────────────────────── */}
                    {activeTab === "activity" && (
                        <Card>
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                    Activity Timeline ({activities.length})
                                </h3>
                                <div className="flex gap-1">
                                    {(["all", "status", "comment", "assignment", "view"] as const).map((f) => (
                                        <button
                                            key={f}
                                            onClick={() => setActivityFilter(f)}
                                            className={`rounded-lg px-2.5 py-1 text-xs font-medium capitalize transition-all ${
                                                activityFilter === f
                                                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                                    : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                                            }`}
                                        >
                                            {f === "all" ? "All" : f}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-4">
                                <ActivityTimeline activities={activities} filterType={activityFilter} />
                            </div>
                        </Card>
                    )}

                    {/* ── Related Events Tab ─────────────────── */}
                    {activeTab === "related" && (
                        <Card>
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                {t("tickets.relatedEvents")} ({relatedEvents.length})
                            </h3>
                            {relatedEvents.length === 0 && (
                                <p className="mt-4 text-sm italic text-zinc-400">No related events.</p>
                            )}
                            <div className="mt-4 space-y-3">
                                {relatedEvents.map((event) => (
                                    <button
                                        key={event.id}
                                        onClick={() => {
                                            if (event.system === "soc")
                                                onNavigateDetail({ view: "soc-alert-detail", detailId: event.id });
                                            else if (event.system === "pentests")
                                                onNavigateDetail({
                                                    view: "pentest-finding-detail",
                                                    detailId: event.id,
                                                });
                                        }}
                                        className="flex w-full items-center gap-4 rounded-xl border border-zinc-200/50 bg-white/60 p-4 text-left transition-all hover:border-blue-300/50 hover:shadow-md hover:shadow-blue-500/10 dark:border-zinc-700/50 dark:bg-zinc-800/60 dark:hover:border-blue-700/50"
                                    >
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-400/20 to-purple-400/20 dark:from-blue-500/20 dark:to-purple-500/20">
                                            <ShieldExclamationIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <Badge color={VIEW_COLORS[event.system]}>
                                                    {VIEW_LABELS[event.system]}
                                                </Badge>
                                                <span className="font-mono text-xs text-zinc-400">{event.id}</span>
                                                <Badge color={priorityColor[event.severity]}>{event.severity}</Badge>
                                            </div>
                                            <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-white">
                                                {event.title}
                                            </p>
                                            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                                                {event.details}
                                            </p>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <span className="font-mono text-xs text-zinc-400">{event.time}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* ── Attachments Tab ─────────────────────── */}
                    {activeTab === "attachments" && (
                        <Card>
                            <AttachmentsTab />
                        </Card>
                    )}
                </div>

                {/* ── Right Sidebar ───────────────────────────── */}
                <div className="space-y-6">
                    {/* SLA Countdown */}
                    <SlaCountdown priority={ticket.priority} createdAt={ticket.createdAt} status={ticket.status} />

                    {/* Quick Actions */}
                    <ActionsSidebar />

                    {/* Metadata */}
                    <Card>
                        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">{t("tickets.metadata")}</h3>
                        <dl className="mt-3 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <dt className="text-zinc-500">{t("tickets.colAssignee")}</dt>
                                <dd className="flex items-center gap-1.5 font-medium text-zinc-900 dark:text-white">
                                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-purple-500 text-[8px] font-bold text-white">
                                        {ticket.assignee
                                            .split(" ")
                                            .map((n) => n[0])
                                            .join("")}
                                    </div>
                                    {ticket.assignee}
                                </dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-zinc-500">{t("tickets.reporter")}</dt>
                                <dd className="font-medium text-zinc-900 dark:text-white">{ticket.reporter}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-zinc-500">{t("tickets.colCreated")}</dt>
                                <dd className="font-mono text-xs text-zinc-600 dark:text-zinc-400">
                                    {ticket.createdAt}
                                </dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-zinc-500">{t("tickets.updated")}</dt>
                                <dd className="font-mono text-xs text-zinc-600 dark:text-zinc-400">
                                    {ticket.updatedAt}
                                </dd>
                            </div>
                        </dl>
                    </Card>

                    {/* Watchers */}
                    <WatchersSection />

                    {/* Linked Tickets */}
                    <LinkedTickets ticketId={ticketId} onNavigateDetail={onNavigateDetail} />

                    {/* Status History */}
                    {ticket.statusHistory.length > 0 && (
                        <Card>
                            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                {t("tickets.statusHistory")}
                            </h3>
                            <div className="mt-3 space-y-0">
                                {ticket.statusHistory.map((sh, i) => (
                                    <div
                                        key={i}
                                        className="relative border-l-2 border-zinc-200/60 py-2 pl-4 dark:border-zinc-700/60"
                                    >
                                        <div className="absolute -left-[4px] top-3 h-2 w-2 rounded-full bg-zinc-400" />
                                        <div className="flex items-center gap-1.5 text-xs">
                                            <Badge color={statusColor[sh.from]}>{sh.from}</Badge>
                                            <span className="text-zinc-400">&rarr;</span>
                                            <Badge color={statusColor[sh.to]}>{sh.to}</Badge>
                                        </div>
                                        <p className="mt-0.5 text-xs text-zinc-400">
                                            {sh.by} &middot; {sh.time}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </div>
            </div>
        </>
    );
}
